import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import { defaultUtcRangeDays } from '../../admin/adminHubUtils';

export default function AdminOrganizationsOfficesPage() {
  const { t } = useLanguage();
  const def = defaultUtcRangeDays(30);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(false);
    try {
      const q = new URLSearchParams({ from: from.trim(), to: to.trim() });
      const res = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN_OPERATIONAL_INSIGHTS)}?${q}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('bad');
      const full = await res.json();
      const offices = full.officeAiConsumptionTop || [];
      setData({ window: full.window, officeAiConsumptionTop: offices });
    } catch {
      setErr(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => {
    const list = data?.officeAiConsumptionTop || [];
    return Array.isArray(list) ? list : [];
  }, [data]);

  const cols = useMemo(
    () => [
      { key: 'office_id', header: t('adminKit.colOfficeId') },
      { key: 'event_count', header: t('adminKit.colEvents') },
      { key: 'usage_units_sum', header: t('adminKit.colUnits') },
    ],
    [t]
  );

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navOrganizations'), to: '/admin/organizations/offices' },
      { label: t('adminHub.navOfficesConsumption') },
    ],
    [t]
  );

  return (
    <AdminHubPageFrame titleKey="adminHub.orgOfficesTitle" descKey="adminHub.orgOfficesDesc" breadcrumbItems={crumbs}>
      <section className="admin-section-card">
        <AdminFilterBar>
          <label>
            {t('adminHub.dateFrom')}
            <input value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            {t('adminHub.dateTo')}
            <input value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </AdminFilterBar>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {data ? (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminKit.officeConsumptionTitle')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.colDateFrom')} value={data.window?.from || '—'} />
              <AdminMetricCard label={t('adminKit.colDateTo')} value={data.window?.to || '—'} />
              <AdminMetricCard label={t('adminKit.officeRowCount')} value={rows.length} />
            </div>
            <AdminDataTable columns={cols} rows={rows} emptyMessage={t('adminKit.tableEmpty')} />
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
